import { Redis } from "@upstash/redis"
import dayjs from "dayjs"
import chunk from "lodash/chunk"

import { retry, sleep } from "../helper"
import { RedisItem } from "./type"

const ALL_PATHS = `ALL_PATHS`
const GROUP_SPLIT = " "
const PAGE_SIZE = 100
const SLEEP_TIME = 1000
const EX = 259200 //3 * 24 * 60 * 60
const TYPES = ["product", "category", "service", "market", "shop_the_look"] as const
export class RedisCache {
    private redis: Redis

    constructor(public logger: (msg: any) => void = console.log) {
        try {
            this.redis = Redis.fromEnv()
        } catch (error) {
            this.logger(`Failed to initialize Redis: ${error}`)
            throw error
        }
    }

    public write = async (locale: string, pages: RedisItem[]) => {
        await this.setAll(pages)
        await Promise.all(TYPES.map((type) => this.setGroup(locale, pages, type)))
    }
    private failArr: RedisItem[] = []
    private setGroup = async (locale: string, pages: RedisItem[], type: RedisItem["type"]) => {
        const paths = pages.filter((i) => i.type === type).map((i) => i.path)
        this.logger(`set ${locale} ${type} pages: ${paths.length}`)
        await this.redis.set(this.key(locale, `${ALL_PATHS}:${type}`), paths.join(GROUP_SPLIT))
    }
    private getGroup = async (locale: string, type: RedisItem["type"]) => {
        const pages = await this.redis
            .get<string>(this.key(locale, `${ALL_PATHS}:${type}`))
            .then((res) => res?.split(GROUP_SPLIT) || [])
        this.logger(`get ${locale} ${type} pages: ${pages.length}`)
        return pages
    }
    public getGroups = async (locale: string, types: RedisItem["type"][]) => {
        const pages = await Promise.all(types.map((type) => this.getGroup(locale, type)))
        return pages.flat()
    }
    private async setChunk(pages: RedisItem[]) {
        const total = pages.length
        const pagesChunk = chunk(pages, PAGE_SIZE)

        this.logger(`Total items: ${total}`)
        this.logger(`Total chunks: ${pagesChunk.length}`)
        for (let group = 0; group < pagesChunk.length; group++) {
            await Promise.all(pagesChunk[group].map((page) => this.set(page)))
            this.logger(`(${group + 1}/${pagesChunk.length}) Group cache set`)
            await sleep(SLEEP_TIME)
        }
    }
    public setAll = async (pages: RedisItem[]) => {
        this.failArr = []
        await this.setChunk(pages)
        await this.reSet()
        this.logger(`Write ${pages.length} pages to redis success`)
    }
    private reSet = async (n = 0) => {
        if (n > 3) {
            this.logger(`[ReSet] reset time over 3`)
            return
        }
        if (this.failArr.length) {
            const failArr = [...this.failArr]
            this.failArr = []
            await this.setChunk(failArr)
            await this.reSet(n + 1)
        }
    }
    private set = async (page: RedisItem): Promise<RedisItem | null> => {
        try {
            await this.redis.set<RedisItem>(
                this.key(page.locale, page.path.replace(/^\//, "")),
                { ...page, t: new Date().getTime() },
                { ex: EX }
            )
            return page
        } catch (error) {
            this.logger(error)
            this.failArr.push(page)
            return null
        }
    }

    public fetch = async (urlPath: string, locale: string): Promise<RedisItem | null> => {
        return this.redis.get<RedisItem>(this.key(locale, urlPath))
    }
    public getProductId = async (path: string, locale: string) => {
        const redisItem = await this.fetch(path, locale)
        if (redisItem?.type !== "product") return
        return {
            id: redisItem.deps.ct.id,
            path,
            locale,
        }
    }
    key(locale: string, path: string) {
        return `slug:${locale.toLowerCase()}:${path}`
    }
}

export interface InventoryRecord {
    allCount: number
    zeroCount: number
    lastUpdated: number //timestamp
    alerted: number //timestamp
}

export class ZeroQuantityAlertCache {
    private redis: Redis

    constructor() {
        this.redis = Redis.fromEnv()
    }
    private key(timestamp: string) {
        const formattedKey = dayjs(timestamp).format("YYYYMMDD-HH")
        return `zero-quantity-alert:${formattedKey}`
    }
    public get = async (timestamp: string) => {
        const initialRecord: InventoryRecord = { allCount: 0, zeroCount: 0, lastUpdated: 0, alerted: 0 }
        return (await this.redis.get<InventoryRecord>(this.key(timestamp)).catch(() => null)) || initialRecord
    }
    public write = async (timestamp: string, record: InventoryRecord) => {
        return retry(() => this.redis.set(this.key(timestamp), record, { ex: EX }))
    }
}
